import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { ArrowRight, Sparkles, FileSearch, Calculator, LayoutDashboard, MessageSquareQuote } from "lucide-react";

const Feature = ({ icon: Icon, title, blurb, tag }) => (
  <div className="p-8 rounded-2xl bg-[#141414] border border-white/5 hover:border-[#CCFF00]/30 transition-colors">
    <div className="flex items-center justify-between mb-6">
      <div className="w-11 h-11 rounded-xl bg-[#CCFF00]/10 border border-[#CCFF00]/20 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#CCFF00]" />
      </div>
      <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{tag}</span>
    </div>
    <h3 className="font-display font-black text-2xl mb-2 tracking-tight">{title}</h3>
    <p className="text-neutral-400 text-sm leading-relaxed">{blurb}</p>
  </div>
);

export default function Landing() {
  return (
    <div className="relative">
      <SEO
        title="Every deal starts with a DAP"
        description="The Creator Business OS. Live media kit, AI contract review, deal copilot, and 15 insider playbooks — free to start."
        path="/"
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#CCFF00]/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-6 pt-24 pb-32 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-8">
            <Sparkles className="w-3 h-3 text-[#CCFF00]" />
            <span className="text-xs font-mono uppercase tracking-widest text-neutral-300">
              The Creator Business OS
            </span>
          </div>

          <h1
            className="font-display font-black text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tighter max-w-5xl"
            data-testid="landing-hero-title"
          >
            Every brand deal <br />
            starts with a{" "}
            <span className="inline-block px-4 py-1 bg-[#CCFF00] text-black rounded-2xl -rotate-2">DAP</span>.
          </h1>

          <p className="mt-10 max-w-2xl text-lg md:text-xl text-neutral-400 leading-relaxed">
            Live media kit. AI contract review. Deal copilot. The missing agent + lawyer + accountant for
            creators earning under $250k. Free to start.
          </p>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/signup" data-testid="hero-cta-signup">
              <Button
                size="lg"
                className="rounded-full h-14 px-8 bg-[#CCFF00] text-black hover:bg-[#b8e600] font-semibold text-base hover:scale-105 transition-transform"
              >
                Claim your handle <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/playbooks" data-testid="hero-cta-playbooks">
              <Button
                size="lg"
                variant="ghost"
                className="rounded-full h-14 px-8 border border-white/15 hover:bg-white/5 text-base"
              >
                Browse 15 free playbooks →
              </Button>
            </Link>
          </div>

          <div className="mt-16 flex flex-wrap gap-x-10 gap-y-3 text-sm text-neutral-500 font-mono">
            <span>◆ Free live media kit</span>
            <span>◆ 1 contract scan / mo</span>
            <span>◆ Pro from $29/mo</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-16 max-w-3xl">
          <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-4">// what dap does</p>
          <h2 className="font-display font-black text-5xl md:text-6xl tracking-tighter leading-none">
            Handle every deal <br />
            like you have a team.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Feature
            icon={LayoutDashboard}
            title="Live Media Kit"
            tag="free · public"
            blurb="A public link brands love. dap.app/@yourname. Big confident numbers, past deals, contact button. Updates as your stats grow."
          />
          <Feature
            icon={Calculator}
            title="What Am I Worth?"
            tag="free · shareable"
            blurb="AI rate calculator built on 2026 creator-economy pricing. Enter your stats, get low / mid / high in seconds."
          />
          <Feature
            icon={FileSearch}
            title="Contract Red Flag Scanner"
            tag="free · 1/mo"
            blurb="Upload a brand contract PDF. Claude Sonnet reads it and flags exclusivity, usage rights, payment terms, indemnity — with plain-English fixes."
          />
          <Feature
            icon={MessageSquareQuote}
            title="Deal Copilot"
            tag="pro"
            blurb="Paste a brand's pitch email. Get a warm, confident counter-offer draft in your voice. Streaming reply — you edit, you send."
          />
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-6 py-24 border-t border-white/5">
        <div className="mb-16">
          <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-4">// pricing</p>
          <h2 className="font-display font-black text-5xl md:text-6xl tracking-tighter">
            Free to start. Upgrade at money moments.
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <Link to="/signup" data-testid="landing-plan-free">
            <div className="p-8 rounded-2xl bg-[#141414] border border-white/5 hover:border-white/20 transition-colors h-full">
              <p className="font-mono text-xs uppercase tracking-widest text-neutral-500 mb-2">Free</p>
              <p className="font-display font-black text-5xl mb-4">$0</p>
              <ul className="space-y-2 text-sm text-neutral-400">
                <li>◆ Live Media Kit</li>
                <li>◆ Rate Calculator</li>
                <li>◆ 1 contract scan / month</li>
              </ul>
            </div>
          </Link>
          <Link to="/billing" data-testid="landing-plan-pro">
            <div className="p-8 rounded-2xl bg-[#CCFF00] text-black relative overflow-hidden h-full hover:scale-[1.02] transition-transform">
              <div className="absolute top-4 right-4 font-mono text-[10px] uppercase tracking-widest bg-black text-[#CCFF00] px-2 py-1 rounded">
                Popular
              </div>
              <p className="font-mono text-xs uppercase tracking-widest text-black/70 mb-2">Pro</p>
              <p className="font-display font-black text-5xl mb-1">
                $29<span className="text-lg">/mo</span>
              </p>
              <p className="text-xs mb-4 text-black/70">Everything to close deals faster.</p>
              <ul className="space-y-2 text-sm">
                <li>◆ Deal Copilot chat</li>
                <li>◆ Unlimited contract scans</li>
                <li>◆ Campaign reports</li>
                <li>◆ Deal pipeline</li>
              </ul>
            </div>
          </Link>
          <Link to="/billing" data-testid="landing-plan-business">
            <div className="p-8 rounded-2xl bg-[#141414] border border-white/5 hover:border-white/20 transition-colors h-full">
              <p className="font-mono text-xs uppercase tracking-widest text-neutral-500 mb-2">Business</p>
              <p className="font-display font-black text-5xl mb-4">
                $59<span className="text-lg">/mo</span>
              </p>
              <ul className="space-y-2 text-sm text-neutral-400">
                <li>◆ Everything in Pro</li>
                <li>◆ Tax + income tracker</li>
                <li>◆ Multi-brand dashboard</li>
                <li>◆ Priority AI models</li>
              </ul>
            </div>
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10 text-center text-xs text-neutral-500 font-mono">
        DAP · Begins and ends with a DAP. · 2026
      </footer>
    </div>
  );
}
